/**
 * Investor Portal API client — separate axios instance + in-memory token.
 * Mirrors portalApi.js (startup) but uses /api/portal/investor base + distinct cookie.
 */
import axios from "axios";
import silentRefresh from "@/lib/silentRefresh";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const INV_PORTAL_API = `${BACKEND_URL}/api/portal/investor`;

let _token = null;
const REFRESH_FAILED = "wit:investor-refresh-failed";
export const INV_REFRESH_FAILED = REFRESH_FAILED;

export function setInvAccessToken(t) { _token = t || null; }
export function getInvAccessToken() { return _token; }

export const investorPortalApi = axios.create({
  baseURL: INV_PORTAL_API,
  timeout: 20000,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

investorPortalApi.interceptors.request.use((config) => {
  if (_token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${_token}`;
  }
  return config;
});

let _refreshing = null;
investorPortalApi.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config || {};
    const status = error.response?.status;
    if (
      status === 401 &&
      !original._isRetry &&
      !original.url?.includes("/session/refresh") &&
      !original.url?.includes("/magic-link") &&
      !original.url?.includes("/2fa")
    ) {
      try {
        if (!_refreshing) {
          _refreshing = silentRefresh(`${INV_PORTAL_API}/session/refresh`).finally(() => {
            setTimeout(() => { _refreshing = null; }, 0);
          });
        }
        const data = await _refreshing;
        if (!data?.access_token) throw new Error("refresh failed");
        setInvAccessToken(data.access_token);
        original._isRetry = true;
        original.headers = original.headers || {};
        original.headers.Authorization = `Bearer ${data.access_token}`;
        return investorPortalApi(original);
      } catch (_e) {
        setInvAccessToken(null);
        window.dispatchEvent(new Event(REFRESH_FAILED));
      }
    }
    return Promise.reject(error);
  },
);

export default investorPortalApi;
